import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import MasterLinkPrompt from '../components/MasterLinkPrompt';
import Layout from '../components/Layout';
import LoiGeneratorDialog from '../components/LoiGeneratorDialog';
import { recordFactsOf } from '../components/loi/recordFacts';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import type {
  Property,
  Comp,
  ValuationResult,
  PropertyResponse,
  CompsResponse,
  ValuationResponse,
  CreateCompInput,
  PropertyType,
  PropertyStatus,
  DealAnalysis,
  DealAnalysisResponse,
} from '../types';
import CompDetailModal from '../components/CompDetailModal';
import PropertyDocumentsPanel from '../components/PropertyDocumentsPanel';
import InvestmentAnalysis from '../components/InvestmentAnalysis';
import type { PoolExitCap } from '../utils/compEvidence';
import Breadcrumbs from '../components/Breadcrumbs';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  PropertyTypeBadge,
  StatsCard,
  Metric,
  Input,
  Select,
  EmptyState,
} from '../components/ui';
import { CompAnalysisCharts } from '../components/charts';
import PhotoGallery from '../components/PhotoGallery';
import { STATUS_OPTIONS } from '../components/StatusBadge';
import { generateValuationSummaryPDF } from '../utils/pdfExport';
import { describeOutcome } from '../utils/exportFiling';
import ProposalGeneratorDialog from '../components/ProposalGeneratorDialog';
import BrochureGeneratorDialog, { type BrochurePrefill } from '../components/BrochureGeneratorDialog';
import { useSetPageSubject, type PageSubject } from '../contexts/PageSubjectContext';
import { usePageAction, type PageActionFields } from '../contexts/PageActionContext';
import { displayDate } from '../utils/dates';

interface Photo {
  id: string;
  file_name: string;
  file_path: string;
  photo_type: string;
  caption?: string;
  is_primary: boolean;
  url: string;
}

interface Transaction {
  id: string;
  transaction_type: string;
  transaction_date?: string;
  recording_date?: string;
  sale_price?: number;
  price_per_sf?: number;
  cap_rate?: number;
  noi?: number;
  buyer_name?: string;
  seller_name?: string;
  broker_name?: string;
  broker_company?: string;
  tenant_name?: string;
  lease_type?: string;
  lease_term_months?: number;
  rent_per_sf_year?: number;
  lease_start_date?: string;
  lease_end_date?: string;
  asking_price?: number;
  asking_price_per_sf?: number;
  days_on_market?: number;
  listing_status?: string;
  asking_cap_rate?: number;
  lender?: string;
  loan_amount?: number;
  loan_type?: string;
  interest_rate?: number;
  maturity_date?: string;
  source?: string;
  notes?: string;
  created_at?: string;
}

const formatDateStr = (value?: string | null) => {
  if (!value) return '-';
  return displayDate(value, { year: 'numeric', month: 'short', day: 'numeric' }) ?? '-';
};

const fmtCurrency = (value?: number | null) => {
  if (value === null || value === undefined) return 'N/A';
  return `$${value.toLocaleString()}`;
};

function TxField({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value || value === '-' || value === 'N/A') return null;
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900">{value}</dd>
    </div>
  );
}

function TransactionCard({ tx }: { tx: Transaction }) {
  const [expanded, setExpanded] = useState(false);

  const typeBadge: Record<string, { color: string; label: string }> = {
    sale: { color: 'bg-success-wash text-success', label: 'Sale' },
    lease: { color: 'bg-info-wash text-info', label: 'Lease' },
    listing: { color: 'bg-gray-100 text-gray-800', label: 'Listing' },
    off_market: { color: 'bg-gray-100 text-gray-800', label: 'Off Market' },
  };
  const badge = typeBadge[tx.transaction_type] || typeBadge.off_market;

  const headline = tx.transaction_type === 'sale'
    ? fmtCurrency(tx.sale_price)
    : tx.transaction_type === 'lease'
    ? (tx.tenant_name || 'Lease')
    : tx.transaction_type === 'listing'
    ? `Asking ${fmtCurrency(tx.asking_price)}`
    : 'Off Market';

  const subline = tx.transaction_type === 'sale'
    ? [tx.price_per_sf ? `$${tx.price_per_sf.toFixed(0)}/SF` : null, tx.cap_rate ? `${tx.cap_rate.toFixed(2)}% Cap` : null].filter(Boolean).join(' · ')
    : tx.transaction_type === 'lease'
    ? [tx.lease_type, tx.rent_per_sf_year ? `$${tx.rent_per_sf_year.toFixed(2)}/SF/yr` : null].filter(Boolean).join(' · ')
    : tx.transaction_type === 'listing'
    ? [tx.asking_price_per_sf ? `$${tx.asking_price_per_sf.toFixed(0)}/SF` : null, tx.days_on_market ? `${tx.days_on_market} DOM` : null, tx.listing_status].filter(Boolean).join(' · ')
    : '';

  const hasSaleDetails = tx.transaction_type === 'sale' && (tx.noi || tx.buyer_name || tx.seller_name || tx.recording_date);
  const hasLeaseDetails = tx.transaction_type === 'lease' && (tx.lease_term_months || tx.lease_start_date || tx.lease_end_date || tx.rent_per_sf_year);
  const hasListingDetails = tx.transaction_type === 'listing' && (tx.asking_cap_rate || tx.days_on_market || tx.listing_status);
  const hasParties = tx.buyer_name || tx.seller_name || tx.broker_name || tx.broker_company;
  const hasFinancing = tx.lender || tx.loan_amount || tx.loan_type || tx.interest_rate || tx.maturity_date;
  const hasMeta = tx.source || tx.notes;
  const hasDetails = hasSaleDetails || hasLeaseDetails || hasListingDetails || hasParties || hasFinancing || hasMeta;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${expanded ? 'border-gray-300 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}>
      <button
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-5 py-4 text-left ${hasDetails ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-4 min-w-0">
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${badge.color}`}>
            {badge.label}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{headline}</p>
            {subline && <p className="text-xs text-gray-500 mt-0.5">{subline}</p>}
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0 ml-4">
          <span className="text-sm text-gray-500">{formatDateStr(tx.transaction_date)}</span>
          {hasDetails && (
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </button>

      {expanded && hasDetails && (
        <div className="border-t bg-gray-50 px-5 py-4 space-y-5">
          {tx.transaction_type === 'sale' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sale Details</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Sale Price" value={fmtCurrency(tx.sale_price)} />
                <TxField label="Price/SF" value={tx.price_per_sf ? `$${tx.price_per_sf.toFixed(2)}` : null} />
                <TxField label="Cap Rate" value={tx.cap_rate ? `${tx.cap_rate.toFixed(2)}%` : null} />
                <TxField label="NOI" value={fmtCurrency(tx.noi)} />
                <TxField label="Recording Date" value={formatDateStr(tx.recording_date)} />
              </dl>
            </div>
          )}

          {tx.transaction_type === 'lease' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Lease Details</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Tenant" value={tx.tenant_name} />
                <TxField label="Lease Type" value={tx.lease_type} />
                <TxField label="Rent/SF/Yr" value={tx.rent_per_sf_year ? `$${tx.rent_per_sf_year.toFixed(2)}` : null} />
                <TxField label="Term" value={tx.lease_term_months ? `${tx.lease_term_months} months` : null} />
                <TxField label="Start Date" value={formatDateStr(tx.lease_start_date)} />
                <TxField label="End Date" value={formatDateStr(tx.lease_end_date)} />
              </dl>
            </div>
          )}

          {tx.transaction_type === 'listing' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Listing Details</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Asking Price" value={fmtCurrency(tx.asking_price)} />
                <TxField label="Asking $/SF" value={tx.asking_price_per_sf ? `$${tx.asking_price_per_sf.toFixed(0)}` : null} />
                <TxField label="Asking Cap" value={tx.asking_cap_rate ? `${tx.asking_cap_rate.toFixed(2)}%` : null} />
                <TxField label="Days on Market" value={tx.days_on_market?.toString()} />
                <TxField label="Status" value={tx.listing_status} />
              </dl>
            </div>
          )}

          {hasParties && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Parties</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Buyer" value={tx.buyer_name} />
                <TxField label="Seller" value={tx.seller_name} />
                <TxField label="Broker" value={tx.broker_name} />
                <TxField label="Brokerage" value={tx.broker_company} />
              </dl>
            </div>
          )}

          {hasFinancing && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Financing</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Lender" value={tx.lender} />
                <TxField label="Loan Amount" value={fmtCurrency(tx.loan_amount)} />
                <TxField label="Loan Type" value={tx.loan_type} />
                <TxField label="Interest Rate" value={tx.interest_rate ? `${tx.interest_rate}%` : null} />
                <TxField label="Maturity" value={formatDateStr(tx.maturity_date)} />
                {tx.loan_amount && tx.sale_price ? (
                  <TxField label="LTV" value={`${((tx.loan_amount / tx.sale_price) * 100).toFixed(0)}%`} />
                ) : null}
              </dl>
            </div>
          )}

          {hasMeta && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Source & Notes</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Source" value={tx.source?.replace('_', ' ')} />
                {tx.notes && (
                  <div className="col-span-full">
                    <dt className="text-xs text-gray-500">Notes</dt>
                    <dd className="text-sm text-gray-700 mt-0.5">{tx.notes}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState<Property | null>(null);
  const [searchParams] = useSearchParams();
  // NN0: the way back to the deal. The URL wins; the row's own memory is the second way in.
  const dealIdFromUrl = searchParams.get('deal');
  const [backDeal, setBackDeal] = useState<{ id: string; name: string } | null>(null);
  // NN0b: "Start a deal" from this analyzer row. Hidden once the row remembers a deal - the
  // Back to deal link above covers that case.
  const [startingDeal, setStartingDeal] = useState(false);
  const startDeal = async () => {
    if (!id || startingDeal) return;
    setStartingDeal(true);
    try {
      const result: any = await api.startDealFromProperty({ property_id: id });
      const dealId = result?.deal?.id;
      if (dealId) navigate(`/crm/deals/${dealId}`);
    } catch (err) {
      console.error('Could not start a deal from this property:', err);
      alert(err instanceof Error ? err.message : 'Could not start the deal');
    } finally {
      setStartingDeal(false);
    }
  };
  useEffect(() => {
    const dealId = dealIdFromUrl ?? (property as any)?.additional_data?.crm_deal_id ?? null;
    if (!dealId) { setBackDeal(null); return; }
    let live = true;
    api.getCrmDeal(dealId)
      .then((r: any) => { if (live) setBackDeal({ id: dealId, name: r.deal?.deal_name ?? 'deal' }); })
      .catch(() => { if (live) setBackDeal({ id: dealId, name: 'deal' }); });
    return () => { live = false; };
  }, [dealIdFromUrl, property]);
  useEffect(() => {
    // Scroll to #analysis / #comps / #valuation once the page has rendered its sections.
    if (!property || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (el) el.scrollIntoView({ block: 'start' });
  }, [property]);
  const [comps, setComps] = useState<Comp[]>([]);
  // Market fallback for the exit cap. Linked comps win when they carry a cap rate;
  // measured 2026-08-01, no linked comp in the database carries one, so in practice
  // this is what backs the suggestion today.
  const [poolExitCap, setPoolExitCap] = useState<PoolExitCap | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [valuation, setValuation] = useState<ValuationResult | null>(null);
  /**
   * The AI valuation's WRITTEN analysis, open or closed. Remembered per device: he should never
   * have to re-open it twice, which is half of why a fold feels like something was taken away.
   * ⚠️ localStorage can throw outright (private windows, blocked site data), so both directions
   * are wrapped and the page renders correctly with no stored value.
   */
  const [showNarrative, setShowNarrative] = useState<boolean>(() => {
    try { return localStorage.getItem('apex.valuationNarrative') === 'open'; } catch { return false; }
  });
  const toggleNarrative = () => {
    setShowNarrative((v) => {
      try { localStorage.setItem('apex.valuationNarrative', v ? 'closed' : 'open'); } catch { /* ignore */ }
      return !v;
    });
  };
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [addingComp, setAddingComp] = useState(false);
  const [showAddComp, setShowAddComp] = useState(false);
  /**
   * DOC2c. The header button no longer owns a dialog - it raises this token and the ONE dialog,
   * inside `PropertyDocumentsPanel`, opens.
   *
   * ⭐⭐⭐ THE BACKLOG CALLED THE TWO DOORS "cosmetic, deliberate" AND MEASURED THEM AS "the same
   * dialog". They were two SEPARATE `UploadDocumentModal` instances whose props agreed by luck -
   * `extractOnUpload` and `targetKind` explicit in the panel, defaulted here. ⛔⛔ **And the
   * refresh never agreed: this page's callback reloaded the PAGE, while the panel loads its own
   * documents - so uploading through the header left the Documents panel below showing the old
   * list until a reload.** One dialog now, so they cannot drift again.
   */
  const [addDocToken, setAddDocToken] = useState(0);
  const [showLOIForm, setShowLOIForm] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [error, setError] = useState('');

  // Multi-comp upload state
  const [uploadingComps, setUploadingComps] = useState(false);
  const [compUploadProgress, setCompUploadProgress] = useState<{
    current: number;
    total: number;
    currentFile: string;
    results: { file: string; success: boolean; error?: string }[];
  } | null>(null);

  // Comp detail modal state
  const [selectedComp, setSelectedComp] = useState<Comp | null>(null);

  // Deal analysis state
  const [dealAnalysis, setDealAnalysis] = useState<DealAnalysis | null>(null);
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [txDebug, setTxDebug] = useState<any>(null);

  // Property edit state
  const [editingProperty, setEditingProperty] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, any>>({});

  // Listing proposal modal state
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [showBrochureForm, setShowBrochureForm] = useState(false);
  /** Built from the property already on screen - no extra request. */
  const propertySubject: PageSubject | null = property
    ? {
        type: 'property',
        id: property.id,
        label: [property.address, property.city].filter(Boolean).join(', ') || 'Property',
        facts: [
          // ⚠️ Field names checked against the `Property` interface in types/index.ts. It has
          // `price`, `cap_rate` and `zoning`; it has NO `asking_price` - reaching for that name
          // fails tsc, and the frontend tsconfig typechecks test files too, so it would break
          // the Vercel build while vitest stayed green.
          ...(property.property_type ? [{ label: 'Type', value: String(property.property_type) }] : []),
          ...(property.building_size ? [{ label: 'Building', value: `${Number(property.building_size).toLocaleString('en-US')} SF` }] : []),
          ...(property.price ? [{ label: 'Price', value: Number(property.price).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) }] : []),
          ...(property.cap_rate ? [{ label: 'Cap rate', value: `${property.cap_rate}%` }] : []),
          ...(property.zoning ? [{ label: 'Zoning', value: property.zoning }] : []),
        ],
      }
    : null;
  useSetPageSubject(propertySubject);
  /**
   * 🚨 WHAT THE RECORD CAN FILL IN, FETCHED - NOT hand-built from the row on this page.
   *
   * `GET /api/brochures/prefill/:id` returns the hero photo, the site plan page and the suggested
   * PROPERTY SUMMARY facts. It was built, tested and deployed, and then every page passed a
   * hand-rolled identity object instead and never called it - so the whole "the record fills the
   * brochure" feature reached nobody. Count the PAGES in the preview to tell the two apart.
   *
   * ⚠️ THIS page's id is an ANALYZER `properties` id, not a `master_properties` id, and the two
   * tables have no key between them. The endpoint resolves either, by id - but the analyzer table
   * has no coordinates, so a property opened HERE gets no AREA DEMOGRAPHICS page.
   */
  const [brochurePrefill, setBrochurePrefill] = useState<BrochurePrefill | null>(null);

  /** Fetch what the record can supply, then open. A failed fetch still opens with identity -
   *  losing the photos is a smaller harm than refusing to open the editor at all. */
  /**
   * ⛔⛔ THE RECORD'S PREFILL LOADS FIRST AND THE CHAT CHOICES GO ON TOP (the 99th).
   *
   * `getBrochurePrefill` REPLACES the prefill wholesale, so merging his stated format, look or
   * cover words in before it resolved would throw them away a moment later - the same timing
   * shape the exec summary hit on the deal page one session ago.
   */
  const openBrochure = async (fields?: PageActionFields): Promise<void> => {
    setShowBrochureForm(true);
    if (!id) return;
    try {
      const res = await api.getBrochurePrefill(id);
      setBrochurePrefill(res.prefill as BrochurePrefill);
    } catch {
      setBrochurePrefill(null);
    }
    if (fields && Object.keys(fields).length) {
      setBrochurePrefill((prev) => ({ ...(prev ?? {}), ...fields }));
    }
  };

  /** Terms stated to the AI rail. No deal on this page, so nothing prefills the offer itself.
   *  ⛔ Cleared on close - terms stated for one letter must not reappear on the next. */
  const [loiFromChat, setLoiFromChat] = useState<PageActionFields>({});
  const [proposalFromChat, setProposalFromChat] = useState<PageActionFields>({});

  /** The AI rail's doors onto this page's own dialogs. Same functions the buttons call. */
  usePageAction('open_brochure_editor', (fields) => { void openBrochure(fields); });
  usePageAction('open_loi_dialog', (fields) => { setLoiFromChat(fields); setShowLOIForm(true); });
  usePageAction('open_listing_proposal_dialog', (fields) => {
    setProposalFromChat(fields); setShowProposalForm(true);
  });
  const [execSummaryHistory, setExecSummaryHistory] = useState<Array<{
    id: string;
    url: string;
    ownerName: string;
    date: Date;
    fileName: string;
  }>>([]);
  const [execSummaryPreviewUrl, setExecSummaryPreviewUrl] = useState<string | null>(null);

  // Cached logos for PDF exports

  useEffect(() => {
    if (id) {
      loadPropertyData();
    }
  }, [id]);

  // Pre-load logos for PDF exports

  const loadPropertyData = async () => {
    try {
      setLoading(true);
      const [propData, compsData, photosData, analysisData, valuationData, txData, execSumData, exitCapData] = await Promise.all([
        api.getProperty(id!),
        api.getComps(id!).catch(() => ({ comps: [] })),
        api.getPhotos(id!).catch(() => ({ photos: [] })),
        api.getDealAnalysis(id!).catch(() => ({ analysis: null })),
        api.getValuation(id!).catch(() => ({ valuation: null })),
        api.getPropertyTransactions(id!).catch(() => ({ transactions: [] })),
        api.listExecutiveSummaries(id!).catch(() => ({ data: [] })),
        api.getExitCapComps(id!).catch(() => null),
      ]);
      setProperty((propData as PropertyResponse).property);
      setComps((compsData as CompsResponse).comps || []);
      setPoolExitCap(exitCapData as PoolExitCap | null);
      setPhotos((photosData as { photos: Photo[] }).photos || []);
      setDealAnalysis((analysisData as DealAnalysisResponse).analysis || null);
      setTransactions((txData as any).transactions || []);
      setTxDebug((txData as any)._debug || null);
      const savedValuation = (valuationData as any)?.valuation;
      if (savedValuation) {
        setValuation(savedValuation);
      }
      // Load persisted executive summaries
      const savedSummaries = (execSumData as any)?.data || [];
      if (savedSummaries.length > 0) {
        setExecSummaryHistory(savedSummaries.map((s: any) => ({
          id: s.id,
          url: s.file_url,
          ownerName: s.owner_name,
          date: new Date(s.created_at),
          fileName: s.file_name,
        })));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load property';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const loadPhotos = async () => {
    try {
      const photosData = await api.getPhotos(id!);
      setPhotos((photosData as { photos: Photo[] }).photos || []);
    } catch (err) {
      console.error('Failed to load photos:', err);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!property || updatingStatus) return;

    try {
      setUpdatingStatus(true);
      await api.updateProperty(id!, { status: newStatus });
      setProperty({ ...property, status: newStatus as PropertyStatus });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update status';
      setError(message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAnalyze = async () => {
    if (comps.length === 0) {
      setError('Please add at least one comparable sale before running analysis.');
      return;
    }

    try {
      setAnalyzing(true);
      setError('');
      const result = await api.analyzeProperty(id!);
      setValuation((result as ValuationResponse).valuation);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Analysis failed';
      setError(message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAddComp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Prevent double submission
    if (addingComp) return;

    setAddingComp(true);
    setError('');

    const formData = new FormData(e.currentTarget);

    const compData: CreateCompInput = {
      comp_address: formData.get('address') as string,
      comp_city: formData.get('city') as string,
      comp_state: formData.get('state') as string,
      comp_zip_code: (formData.get('zip') as string) || undefined,
      comp_property_type: formData.get('type') as PropertyType,
      comp_square_footage: formData.get('sqft') ? parseInt(formData.get('sqft') as string) : null,
      comp_year_built: formData.get('year') ? parseInt(formData.get('year') as string) : null,
      comp_sale_price: parseFloat(formData.get('price') as string),
      comp_sale_date: formData.get('date') as string,
      comp_price_per_sqft: formData.get('pricesf') ? parseFloat(formData.get('pricesf') as string) : null,
    };

    try {
      await api.addComp(id!, compData);
      setShowAddComp(false);
      await loadPropertyData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add comp';
      setError(message);
    } finally {
      setAddingComp(false);
    }
  };

  const handleDeleteComp = async (compId: string) => {
    if (!window.confirm('Delete this comp?')) return;

    try {
      await api.deleteComp(compId);
      await loadPropertyData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete comp';
      setError(message);
    }
  };

  const handleDeleteProperty = async () => {
    if (!window.confirm('Delete this property? This action cannot be undone.')) return;

    try {
      await api.deleteProperty(id!);
      navigate('/dashboard');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete property';
      setError(message);
    }
  };

  const startEditProperty = () => {
    if (!property) return;
    setEditForm({
      price: property.price || '',
      building_size: property.building_size || '',
      cap_rate: property.cap_rate || '',
      noi: property.noi || '',
      lot_size: property.lot_size || '',
      year_built: property.year_built || '',
      zoning: property.zoning || '',
      parking_spaces: property.parking_spaces || '',
      property_type: property.property_type || '',
      property_subtype: property.property_subtype || '',
      occupancy_rate: property.occupancy_rate || '',
      address: property.address || '',
      city: property.city || '',
      state: property.state || '',
      zip_code: property.zip_code || '',
    });
    setEditingProperty(true);
  };

  const handleSavePropertyEdits = async () => {
    if (!property) return;
    setSavingProperty(true);
    try {
      const updates: Record<string, any> = {};
      const numericFields = ['price', 'building_size', 'cap_rate', 'noi', 'lot_size', 'year_built', 'parking_spaces', 'occupancy_rate'];
      const stringFields = ['zoning', 'property_type', 'property_subtype', 'address', 'city', 'state', 'zip_code'];

      // Only include fields that have values — omit empty ones to pass Zod strict validation
      for (const key of numericFields) {
        const val = editForm[key];
        if (val !== '' && val !== null && val !== undefined) {
          updates[key] = parseFloat(val);
        }
      }
      for (const key of stringFields) {
        if (editForm[key]) {
          updates[key] = editForm[key];
        }
      }

      // Auto-calculate price per sqft
      if (updates.price && updates.building_size) {
        updates.price_per_sqft = Math.round(updates.price / updates.building_size);
      }

      await api.updateProperty(id!, updates);
      setProperty({ ...property, ...updates });
      setEditingProperty(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update property';
      setError(message);
    } finally {
      setSavingProperty(false);
    }
  };

  // Letter dates are business dates - always America/Los_Angeles, never UTC.
  const handleSaveDealAnalysis = async (analysisData: Partial<DealAnalysis>) => {
    try {
      setSavingAnalysis(true);
      const result = await api.saveDealAnalysis(id!, analysisData);
      setDealAnalysis((result as DealAnalysisResponse).analysis);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save analysis';
      setError(message);
    } finally {
      setSavingAnalysis(false);
    }
  };

  const handleCompUpdate = (updatedComp: Comp) => {
    setComps(prev => prev.map(c => c.id === updatedComp.id ? updatedComp : c));
  };




  /** Where the last valuation export went - the Deal Room, the property, or why it was not filed. */
  const [valuationExportNote, setValuationExportNote] = useState<string | null>(null);
  const handleDownloadValuationSummaryPDF = async () => {
    if (!valuation || !property) return;
    const branding = user ? {
      company_name: user.company_name,
      company_logo_url: user.company_logo_url,
      company_phone: user.company_phone,
      company_email: user.company_email,
      company_address: user.company_address,
    } : undefined;
    const outcome = await generateValuationSummaryPDF({ property, valuation, branding });
    setValuationExportNote(describeOutcome(outcome));
  };

  // Handle multi-comp PDF upload
  const handleCompFilesSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const pdfFiles = Array.from(files).filter(f => f.type === 'application/pdf');
    if (pdfFiles.length === 0) {
      setError('Please select PDF files only');
      return;
    }

    setUploadingComps(true);
    setError('');
    setCompUploadProgress({
      current: 0,
      total: pdfFiles.length,
      currentFile: '',
      results: [],
    });

    const results: { file: string; success: boolean; error?: string }[] = [];

    for (let i = 0; i < pdfFiles.length; i++) {
      const file = pdfFiles[i];
      setCompUploadProgress(prev => prev ? {
        ...prev,
        current: i + 1,
        currentFile: file.name,
      } : null);

      try {
        // Read file buffer
        const buffer = await file.arrayBuffer();

        // Upload document with comp type
        const uploadResult: any = await api.uploadDocumentWithBuffer(
          { name: file.name, size: file.size, type: file.type, buffer },
          id,
          'comp'
        );

        // Extract data from document
        await api.extractDocument(uploadResult.document.id);

        results.push({ file: file.name, success: true });
      } catch (err: any) {
        console.error(`Failed to upload comp ${file.name}:`, err);
        results.push({ file: file.name, success: false, error: err.message });
      }

      setCompUploadProgress(prev => prev ? { ...prev, results: [...results] } : null);
    }

    // Reset file input
    e.target.value = '';

    // Refresh comps list
    await loadPropertyData();

    // Show results briefly then clear
    setTimeout(() => {
      setUploadingComps(false);
      setCompUploadProgress(null);
    }, 2000);
  };

  const formatCurrency = (value?: number | null) => {
    if (value === null || value === undefined) return 'N/A';
    return `$${value.toLocaleString()}`;
  };

  const formatNumber = (value?: number | null) => {
    if (value === null || value === undefined) return 'N/A';
    return value.toLocaleString();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-600 font-medium">Loading property details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <EmptyState
          title="Property not found"
          description="The property you're looking for doesn't exist or may have been deleted."
          action={{
            label: 'Back to Portfolio',
            onClick: () => navigate('/dashboard'),
          }}
          icon={
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumb & Header */}
        <div>
          {backDeal && (
            <Link to={`/crm/deals/${backDeal.id}`} className="inline-flex items-center text-sm text-primary-500 hover:underline mb-2">
              Back to deal: {backDeal.name}
            </Link>
          )}
          <Breadcrumbs items={[
            { label: 'My Deals', href: '/dashboard' },
            { label: property.address || 'Property' },
          ]} />
          {/* AIW2 - which Property Database record this row is. */}
          {property.id && <div className="mt-3"><MasterLinkPrompt key={property.id} propertyId={property.id} /></div>}

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                {property.property_type && (
                  <PropertyTypeBadge type={property.property_type} />
                )}
                {property.market && (
                  <Badge variant="default">{property.market}</Badge>
                )}
                {/* Status Badge with dropdown */}
                <div className="relative inline-block">
                  <select
                    value={property.status || 'prospect'}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={updatingStatus}
                    className="appearance-none bg-transparent border-none cursor-pointer focus:outline-none focus:ring-0 pr-6"
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {updatingStatus && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/75">
                      <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              </div>
              <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">
                {property.address || 'Vacant Land'}
              </h1>
              <p className="text-gray-500 mt-1">
                {[property.city, property.state, property.zip_code].filter(Boolean).join(', ')}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => setAddDocToken((t) => t + 1)}
                leftIcon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                }
              >
                Upload Document
              </Button>
              {!(property as any)?.additional_data?.crm_deal_id && (
                <Button variant="outline" onClick={startDeal} disabled={startingDeal} isLoading={startingDeal}>
                  Start a deal
                </Button>
              )}
              {/*
                🚨 THESE TWO LIVED INSIDE `{valuation.pricing_scenarios && ...}` AND SO EXISTED ON
                5 OF 25 PROPERTIES. Measured in the running app: opening `1101 E 1st Street` -
                a real client property with four photographs and an executed contract - showed no
                Brochure / OM button at all, because nobody had ever run an AI valuation on it.
                A marketing document does not depend on an AI pricing opinion, so the gate was
                never a rule, only where the buttons happened to be pasted.

                ⭐ Same defect class as the LOI being reachable from no page he uses: the dialog
                was imported, mounted and offered, and every entry-point test was right - the
                button was simply invisible on the screens he opens.
              */}
              {/* ⚠️ `() =>`, NOT a bare reference. `openBrochure` now takes optional fields, and
                  passing it straight to onClick would hand it the MouseEvent as its prefill -
                  spreading a DOM event into the brochure. `tsc` caught this one. */}
              <Button variant="outline" onClick={() => { void openBrochure(); }}>
                Brochure / OM
              </Button>
              <Button variant="outline" onClick={() => setShowProposalForm(true)}>
                Listing Proposal
              </Button>
              {/*
                🚨 AND THE LOI, WHICH THE COMMENT ABOVE NAMED AND THEN LEFT BEHIND. Joseph,
                2026-09-04: "on my deals, on a deal page i dont see the loi button." Its only
                door was inside `{valuation.wholesale_offer && ...}`, so it appeared only after
                someone had run an AI valuation AND that valuation had returned a wholesale
                offer. An LOI is an offer letter - it depends on a price he can type, never on
                the app having formed a pricing opinion first. The offer block keeps its own
                button, because there the price is already on screen and worth carrying over.
              */}
              <Button variant="outline" onClick={() => setShowLOIForm(true)}>
                Generate LOI
              </Button>
              <Button
                onClick={handleAnalyze}
                disabled={analyzing || comps.length === 0}
                isLoading={analyzing}
                leftIcon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                }
              >
                {analyzing ? 'Analyzing...' : valuation ? 'Re-run AI Valuation' : 'Run AI Valuation'}
              </Button>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 font-bold">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            label="List Price"
            value={formatCurrency(property.price)}
            variant="highlight"
            icon={
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatsCard
            label="CAP Rate"
            value={property.cap_rate ? `${property.cap_rate}%` : '—'}
            icon={
              <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
          />
          <StatsCard
            label="Building Size"
            value={property.building_size ? `${formatNumber(property.building_size)} SF` : '—'}
            icon={
              <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
          />
          <StatsCard
            label="Price / SF"
            value={property.price_per_sqft ? `$${property.price_per_sqft}` : '—'}
            icon={
              <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            }
          />
        </div>

        {/* Reconciliation Insights & Pro Forma Comparison */}
        {(() => {
          const additional = property.additional_data as Record<string, any> | undefined;
          const recon = additional?.reconciliation;
          const pf = additional?.pro_forma_financials;
          const mkt = additional?.market_context;

          if (!recon && !pf && !mkt) return null;

          return (
            <div className="space-y-3">
              {/* Derived Fields Info */}
              {recon?.derived_fields?.length > 0 && (
                <div className="bg-info-wash border border-info/30 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-info mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-info">Smart Analysis: Back-calculated {recon.derived_fields.length} metrics</p>
                      <p className="text-xs text-info mt-0.5">{recon.derived_fields.join(' | ')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Reconciliation Warnings */}
              {recon?.warnings?.filter((w: any) => w.severity === 'warning' || w.severity === 'error').length > 0 && (
                <div className="bg-warning-wash border border-warning/30 rounded-lg p-3">
                  <p className="text-sm font-medium text-warning mb-1">Data Validation Notes</p>
                  {recon.warnings.filter((w: any) => w.severity !== 'info').map((w: any, i: number) => (
                    <p key={i} className="text-xs text-warning">{w.message}</p>
                  ))}
                </div>
              )}

              {/* Current vs Pro Forma Comparison */}
              {pf && (pf.noi || pf.cap_rate || pf.gross_income) && (
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Current vs. Pro Forma (Stabilized)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">NOI</p>
                        <p className="text-sm font-medium text-gray-400">{property.noi ? formatCurrency(property.noi) : '—'} <span className="text-[10px]">current</span></p>
                        <p className="text-sm font-bold text-success">{pf.noi ? formatCurrency(pf.noi) : '—'} <span className="text-[10px] font-normal">stabilized</span></p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">CAP Rate</p>
                        <p className="text-sm font-medium text-gray-400">{property.cap_rate ? `${property.cap_rate}%` : '—'} <span className="text-[10px]">current</span></p>
                        <p className="text-sm font-bold text-success">{pf.cap_rate ? `${pf.cap_rate}%` : '—'} <span className="text-[10px] font-normal">stabilized</span></p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Gross Income</p>
                        <p className="text-sm font-medium text-gray-400">{property.gross_income ? formatCurrency(property.gross_income) : '—'} <span className="text-[10px]">current</span></p>
                        <p className="text-sm font-bold text-success">{pf.gross_income ? formatCurrency(pf.gross_income) : '—'} <span className="text-[10px] font-normal">stabilized</span></p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Occupancy</p>
                        <p className="text-sm font-medium text-gray-400">{property.occupancy_rate ? `${property.occupancy_rate}%` : '—'} <span className="text-[10px]">current</span></p>
                        <p className="text-sm font-bold text-success">{pf.occupancy_rate ? `${pf.occupancy_rate}%` : '—'} <span className="text-[10px] font-normal">target</span></p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Market Context */}
              {mkt && (mkt.nearby_anchors?.length > 0 || mkt.daily_traffic || mkt.demographics) && (
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Market Context</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      {mkt.daily_traffic && (
                        <div>
                          <p className="text-gray-500">Daily Traffic</p>
                          <p className="font-medium">{mkt.daily_traffic.toLocaleString()} vehicles</p>
                        </div>
                      )}
                      {mkt.frontage && (
                        <div>
                          <p className="text-gray-500">Frontage</p>
                          <p className="font-medium">{mkt.frontage}</p>
                        </div>
                      )}
                      {mkt.nearby_anchors?.length > 0 && (
                        <div className="md:col-span-2">
                          <p className="text-gray-500">Nearby Anchors</p>
                          <p className="font-medium">{mkt.nearby_anchors.join(', ')}</p>
                        </div>
                      )}
                      {mkt.demographics && (
                        <>
                          {mkt.demographics.population_3mi && (
                            <div>
                              <p className="text-gray-500">Population (3mi)</p>
                              <p className="font-medium">{mkt.demographics.population_3mi.toLocaleString()}</p>
                            </div>
                          )}
                          {mkt.demographics.median_hh_income_3mi && (
                            <div>
                              <p className="text-gray-500">Median HH Income (3mi)</p>
                              <p className="font-medium">${mkt.demographics.median_hh_income_3mi.toLocaleString()}</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          );
        })()}

        {/* Photo Gallery, and everything else filed on this property */}
        <Card>
          <CardContent className="pt-6">
            <PhotoGallery
              propertyId={id!}
              photos={photos}
              onPhotosChange={loadPhotos}
            />
            {/* DOC0: the documents filed on this property - its own, plus any filed on the
                Property Database record it was created from. UNCONDITIONAL on purpose: the LOI
                button sat behind `{valuation.wholesale_offer && ...}` and Joseph could not find
                it, and 86 documents were invisible for the same reason. Nothing here is gated on
                data the page may not have. */}
            <PropertyDocumentsPanel
              kind="deal"
              propertyId={id!}
              openAddToken={addDocToken}
              onDocumentAdded={loadPropertyData}
            />
          </CardContent>
        </Card>

        <div id="analysis" className="scroll-mt-20" />
        {/* Investment Analysis Engine */}
        {property && (
          <InvestmentAnalysis
            property={property}
            comps={comps}
            poolExitCap={poolExitCap}
            initialData={dealAnalysis || undefined}
            onSave={handleSaveDealAnalysis}
            saving={savingAnalysis}
            photoUrl={(photos.find(p => p.is_primary) || photos.find(p => p.photo_type === 'exterior') || photos[0])?.url}
          />
        )}

        {/* AI Valuation Results */}
        {valuation && (
          <Card variant="elevated">
            <div id="valuation" className="scroll-mt-20" />
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div>
                    <CardTitle>AI Valuation Analysis</CardTitle>
                    <p className="text-sm text-gray-500">Powered by Claude AI</p>
                  </div>
                </div>
                <Badge variant={valuation.confidence_level === 'High' ? 'success' : valuation.confidence_level === 'Medium' ? 'warning' : 'default'} size="md">
                  {valuation.confidence_level} confidence
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Estimated Value Hero - Enhanced */}
              <div className="bg-gradient-to-br from-primary-50 via-primary-100 to-info rounded-xl p-8 mb-8 text-center border border-primary-200 shadow-sm">
                <p className="text-sm font-semibold text-primary-500 uppercase tracking-wide mb-2">Estimated Market Value</p>
                <p className="text-5xl font-extrabold text-primary-900 mb-3">{formatCurrency(valuation.estimated_value)}</p>
                {valuation.value_range && (
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white border border-primary-200 text-sm font-semibold text-primary-500 shadow-sm">
                      {formatCurrency(valuation.value_range.low)}
                    </span>
                    <span className="text-primary-400 font-medium">to</span>
                    <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white border border-primary-200 text-sm font-semibold text-primary-500 shadow-sm">
                      {formatCurrency(valuation.value_range.high)}
                    </span>
                  </div>
                )}
                {valuation.price_per_sqft_estimate && (
                  <p className="text-sm text-primary-500 mt-3">
                    Estimated ${valuation.price_per_sqft_estimate}/SF
                  </p>
                )}
              </div>

              <div className="space-y-8">
                {/*
                  ⭐⭐⭐ THE NUMBERS ARE NEVER BEHIND THIS CONTROL - only the written analysis is.
                  Measured 2026-09-08 at 375x812: this section was **9,883px, 50% of a 19,663px
                  page (24.2 phone screens)**, of which the answer itself - value, range, $/SF -
                  is 220px. The rest is generated prose.

                  ⛔⛔ AND THE LESSON FROM THE DOCUMENTS FOLD APPLIES HERE, WHICH IS WHY THE LABEL
                  COUNTS WHAT IS INSIDE. Hiding his documents behind "Show all 7" read to him as
                  the documents having gone. A control that names its contents - "summary, 5
                  findings, 3 recommendations" - is a door; one that says "show more" is a wall.
                  The difference between this and the documents: a document is an artefact he
                  needs to reach, a paragraph is something read once. Exit pricing stays visible
                  because he prices deals off it.
                */}
                <div>
                  <button
                    type="button"
                    onClick={toggleNarrative}
                    aria-expanded={showNarrative}
                    data-valuation-narrative-toggle
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:bg-gray-50"
                  >
                    {showNarrative ? 'Hide the written analysis' : 'Read the full analysis'}
                    <span className="ml-2 font-normal text-gray-500">
                      {[
                        valuation.analysis ? 'summary' : null,
                        valuation.key_findings?.length ? `${valuation.key_findings.length} findings` : null,
                        valuation.recommendations?.length ? `${valuation.recommendations.length} recommendations` : null,
                        valuation.market_insights ? 'market insights' : null,
                      ].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </div>

                {showNarrative && (
                <div className="space-y-8" data-valuation-narrative>
                {/* Analysis Text */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Analysis Summary</h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{valuation.analysis}</p>
                </div>

                {/* Key Findings - Enhanced cards */}
                {valuation.key_findings && valuation.key_findings.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Key Findings</h3>
                    <div className="space-y-3">
                      {valuation.key_findings.map((finding, i) => (
                        <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100">
                          <div className="flex-shrink-0 w-7 h-7 bg-primary-100 text-primary-500 rounded-full flex items-center justify-center text-sm font-bold">
                            {i + 1}
                          </div>
                          <span className="text-gray-700 leading-relaxed">{finding}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations - Enhanced cards */}
                {valuation.recommendations && valuation.recommendations.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Recommendations</h3>
                    <div className="space-y-3">
                      {valuation.recommendations.map((rec, i) => (
                        <div key={i} className="flex items-start gap-3 bg-success-wash rounded-lg p-4 border border-success/30">
                          <svg className="w-5 h-5 text-success flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700 leading-relaxed">{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {valuation.market_insights && (
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-2">Market Insights</h3>
                    <p className="text-gray-700 leading-relaxed">{valuation.market_insights}</p>
                  </div>
                )}

                </div>
                )}

                {/* Pricing Scenarios - Enhanced */}
                {valuation.pricing_scenarios && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-5">Exit Strategy Pricing</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      {/* Quick Sale */}
                      <div className="bg-warning-wash border border-warning/30 rounded-xl p-5 transition-colors">
                        <div className="flex items-center gap-2 mb-3">
                          <svg className="w-5 h-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span className="font-semibold text-warning">Quick Sale</span>
                        </div>
                        <p className="text-3xl font-bold text-warning mb-1">{formatCurrency(valuation.pricing_scenarios.quick_sale.price)}</p>
                        <p className="text-sm text-warning font-medium">${valuation.pricing_scenarios.quick_sale.price_per_sqft}/SF</p>
                        <div className="border-t border-warning/30 mt-3 pt-3">
                          <p className="text-xs text-warning font-medium">{valuation.pricing_scenarios.quick_sale.timeline}</p>
                          <p className="text-xs text-warning">{valuation.pricing_scenarios.quick_sale.discount_percentage}% below market</p>
                          <p className="text-xs text-gray-600 mt-2 leading-relaxed">{valuation.pricing_scenarios.quick_sale.rationale}</p>
                        </div>
                      </div>

                      {/* Market Sale */}
                      <div className="bg-info-wash border-2 border-info/30 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <svg className="w-5 h-5 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <span className="font-semibold text-info">Market Sale</span>
                          <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-info-wash text-info px-2 py-0.5 rounded-full">Recommended</span>
                        </div>
                        <p className="text-3xl font-bold text-info mb-1">{formatCurrency(valuation.pricing_scenarios.market_sale.price)}</p>
                        <p className="text-sm text-info font-medium">${valuation.pricing_scenarios.market_sale.price_per_sqft}/SF</p>
                        <div className="border-t border-info/30 mt-3 pt-3">
                          <p className="text-xs text-info font-medium">{valuation.pricing_scenarios.market_sale.timeline}</p>
                          <p className="text-xs text-gray-600 mt-2 leading-relaxed">{valuation.pricing_scenarios.market_sale.rationale}</p>
                        </div>
                      </div>

                      {/* Premium Sale */}
                      <div className="bg-success-wash border border-success/30 rounded-xl p-5 transition-colors">
                        <div className="flex items-center gap-2 mb-3">
                          <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                          <span className="font-semibold text-success">Premium Sale</span>
                        </div>
                        <p className="text-3xl font-bold text-success mb-1">{formatCurrency(valuation.pricing_scenarios.premium_sale.price)}</p>
                        <p className="text-sm text-success font-medium">${valuation.pricing_scenarios.premium_sale.price_per_sqft}/SF</p>
                        <div className="border-t border-success/30 mt-3 pt-3">
                          <p className="text-xs text-success font-medium">{valuation.pricing_scenarios.premium_sale.timeline}</p>
                          <p className="text-xs text-success">+{valuation.pricing_scenarios.premium_sale.premium_percentage}% above market</p>
                          <p className="text-xs text-gray-600 mt-2 leading-relaxed">{valuation.pricing_scenarios.premium_sale.rationale}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Valuation Summary — below Exit Strategy Pricing */}
                {valuation.executive_summary && (
                  <div className="bg-info-wash border border-info/30 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="font-semibold text-info">Valuation Summary</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadValuationSummaryPDF}
                        leftIcon={
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        }
                      >
                        Download PDF
                      </Button>
                    </div>
                    {valuationExportNote && (
                      <p className="text-xs text-gray-600 mb-2" data-testid="valuation-export-note">{valuationExportNote}</p>
                    )}
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{valuation.executive_summary}</p>
                  </div>
                )}

                {/* Executive Summaries */}
                {valuation.pricing_scenarios && (
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    {/* Section header */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 rounded-lg">
                          <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">Executive Summaries</h3>
                          {execSummaryHistory.length > 0 && (
                            <p className="text-xs text-gray-500">{execSummaryHistory.length} document{execSummaryHistory.length !== 1 ? 's' : ''} generated</p>
                          )}
                        </div>
                      </div>
                      {/* Both generators now live in the page header, where they render for
                          every property rather than only for one carrying an AI valuation. */}
                    </div>

                    {/* Document list */}
                    {execSummaryHistory.length > 0 ? (
                      <div className="divide-y divide-gray-100">
                        {execSummaryHistory.map((item) => (
                          <div
                            key={item.id}
                            className={`flex items-center gap-4 px-5 py-3 cursor-pointer transition-colors ${
                              execSummaryPreviewUrl === item.url
                                ? 'bg-red-50/60'
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => setExecSummaryPreviewUrl(
                              execSummaryPreviewUrl === item.url ? null : item.url
                            )}
                          >
                            {/* PDF icon */}
                            <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                              execSummaryPreviewUrl === item.url ? 'bg-red-100' : 'bg-gray-100'
                            }`}>
                              <svg className={`w-5 h-5 ${execSummaryPreviewUrl === item.url ? 'text-red-600' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                              </svg>
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{item.ownerName}</p>
                              <p className="text-xs text-gray-500">
                                {item.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                {' at '}
                                {item.date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                              </p>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 shrink-0">
                              {execSummaryPreviewUrl === item.url && (
                                <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">Viewing</span>
                              )}
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="p-1.5 text-gray-400 hover:text-info hover:bg-info-wash rounded-lg transition-colors"
                                title="Download PDF"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-5 py-8 text-center">
                        <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm text-gray-500">No executive summaries yet</p>
                        <p className="text-xs text-gray-400 mt-1">Generate one to get started</p>
                      </div>
                    )}

                    {/* Inline PDF Preview */}
                    {execSummaryPreviewUrl && (
                      <div className="border-t border-gray-200">
                        <div className="flex items-center justify-between bg-gray-50 px-5 py-2 border-b border-gray-100">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Preview</span>
                          <button
                            onClick={() => setExecSummaryPreviewUrl(null)}
                            className="text-xs text-gray-400 hover:text-gray-600 font-medium"
                          >
                            Close Preview
                          </button>
                        </div>
                        <iframe
                          src={execSummaryPreviewUrl}
                          className="w-full"
                          style={{ height: '500px' }}
                          title="Executive Summary PDF Preview"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Wholesale Offer */}
                {valuation.wholesale_offer && (
                  <div className="bg-gray-100 border border-gray-300 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <svg className="w-6 h-6 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="font-semibold text-gray-800">Investor/Wholesale Offer ({valuation.wholesale_offer.arv_percentage}% ARV)</span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setShowLOIForm(true)}
                        leftIcon={
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        }
                      >
                        Generate LOI
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-800">Offer Price</p>
                        <p className="text-3xl font-bold text-gray-800">{formatCurrency(valuation.wholesale_offer.offer_price)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-800">Potential Profit</p>
                        <p className="text-3xl font-bold text-success">{formatCurrency(valuation.wholesale_offer.potential_profit)}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-3">{valuation.wholesale_offer.rationale}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* LOI Generation Form Modal */}
        {/* One LOI form for the whole app - the deal page and the property database use the
            same component. No deal on this page, so the letter downloads rather than files. */}
        <LoiGeneratorDialog
          open={showLOIForm}
          onClose={() => { setShowLOIForm(false); setLoiFromChat({}); }}
          prefill={{
            address: property?.address ?? null,
            city: property?.city ?? null,
            state: property?.state ?? 'CA',
            zip: property?.zip_code ?? null,
            buildingSf: property?.building_size ?? null,
            purchasePrice: valuation?.wholesale_offer?.offer_price ?? null,
            // Last, so a price he just stated beats the computed wholesale offer above it.
            ...loiFromChat,
            // ⭐ Spec 3.9: this record, for the letter to say where it differs from it.
            recordFacts: recordFactsOf({ address: property?.address, city: property?.city, zip: property?.zip_code, buildingSf: property?.building_size }),
          }}
        />

        {/* The analyzer property page's listing-proposal entry - the SAME dialog the deal page
            and the property database use. It replaces a 746-line client-side jsPDF builder whose
            layout was invented rather than read off his letters, and whose per-property-type
            templates asserted key considerations nobody typed. No deal here, so it downloads. */}
        <ProposalGeneratorDialog
          open={showProposalForm}
          onClose={() => setShowProposalForm(false)}
          /* ⭐ DOC-READ (the 157th): the form reads THIS property itself - its record and the documents filed on it, the
             title profile's owners and mailing address among them. Before, this page filled the address, the city and
             an owner field nothing ever writes, and his uploaded profile's owners never reached the letter.
             ⭐ The 98th's rule stands: anything he stated in the chat goes on top. The page still owns WHICH property
             this is - `AssistantPanel` strips the identity fields whenever a record is on screen. */
          recordId={property?.id ?? null}
          prefill={proposalFromChat}
          fallback={{ addressLine: property?.address ?? null, city: property?.city ?? null }}
        />

        {/* The analyzer property page's brochure / OM entry - the third caller of the one
            builder, for the same reason as the proposal: he works on all three screens. */}
        <BrochureGeneratorDialog
          open={showBrochureForm}
          onClose={() => setShowBrochureForm(false)}
          propertyId={property?.id}
          prefill={brochurePrefill ?? {
            // Fallback only - identity from the row already on screen, so a failed prefill fetch
            // still opens a usable editor rather than an empty one.
            addressLine: property?.address ?? null,
            city: property?.city ?? null,
            state: property?.state ?? null,
            zip: (property as any)?.zip_code ?? null,
            county: (property as any)?.county ?? null,
            zoning: (property as any)?.zoning ?? null,
            apn: (property as any)?.apn ?? null,
          }}
        />

        {/* Generated LOI Display */}
        {/* Comp Analysis Charts */}
        {property && comps.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Comp Analysis Visualizations
            </h2>
            <CompAnalysisCharts property={property} comps={comps} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Comparable Sales - Main Content */}
          <div className="lg:col-span-2">
            <Card>
              <div id="comps" className="scroll-mt-20" />
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle>Comparable Sales</CardTitle>
                    <Badge variant="primary">{comps.length}</Badge>
                  </div>
                  <div className="flex gap-2">
                    {/* Hidden file input for multi-comp upload */}
                    <input
                      type="file"
                      id="comp-files-input"
                      accept=".pdf"
                      multiple
                      onChange={handleCompFilesSelect}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('comp-files-input')?.click()}
                      disabled={uploadingComps}
                      leftIcon={
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                      }
                    >
                      Upload
                    </Button>
                    <Button
                      variant={showAddComp ? 'ghost' : 'outline'}
                      size="sm"
                      onClick={() => setShowAddComp(!showAddComp)}
                    >
                      {showAddComp ? 'Cancel' : '+ Add Comp'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Upload Progress */}
                {uploadingComps && compUploadProgress && (
                  <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                      <span className="font-medium text-primary-900">
                        Uploading comp {compUploadProgress.current} of {compUploadProgress.total}
                      </span>
                    </div>
                    {compUploadProgress.currentFile && (
                      <p className="text-sm text-primary-500 mb-2">
                        Processing: {compUploadProgress.currentFile}
                      </p>
                    )}
                    {/* Progress bar */}
                    <div className="w-full bg-primary-200 rounded-full h-2 mb-3">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(compUploadProgress.current / compUploadProgress.total) * 100}%` }}
                      />
                    </div>
                    {/* Results */}
                    {compUploadProgress.results.length > 0 && (
                      <div className="space-y-1">
                        {compUploadProgress.results.map((result, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            {result.success ? (
                              <svg className="w-4 h-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                            <span className={result.success ? 'text-success' : 'text-red-700'}>
                              {result.file}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Add Comp Form */}
                {showAddComp && (
                  <form onSubmit={handleAddComp} className="bg-gray-50 rounded-xl p-5 mb-6 border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-4">Add Comparable Sale</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input name="address" required placeholder="Address" label="Address *" />
                      <Input name="city" required placeholder="City" label="City *" />
                      <Input name="state" required placeholder="CA" maxLength={2} label="State *" />
                      <Input name="zip" placeholder="Zip Code" label="Zip Code" />
                      <Select
                        name="type"
                        label="Property Type *"
                        options={[
                          { value: 'warehouse', label: 'Warehouse' },
                          { value: 'distribution_center', label: 'Distribution Center' },
                          { value: 'manufacturing', label: 'Manufacturing' },
                          { value: 'flex_space', label: 'Flex Space' },
                          { value: 'land', label: 'Land' },
                          { value: 'office', label: 'Office' },
                          { value: 'retail', label: 'Retail' },
                          { value: 'industrial', label: 'Industrial' },
                        ]}
                        placeholder="Select type"
                      />
                      <Input name="sqft" type="number" placeholder="Square Feet" label="Square Feet" />
                      <Input name="year" type="number" placeholder="2020" label="Year Built" />
                      <Input name="price" type="number" step="0.01" required placeholder="Sale Price" label="Sale Price *" leftAddon="$" />
                      <Input name="date" type="date" required label="Sale Date *" />
                      <Input name="pricesf" type="number" step="0.01" placeholder="Price/SF" label="Price per SF" leftAddon="$" />
                    </div>
                    <div className="mt-5 flex justify-end gap-3">
                      <Button type="button" variant="ghost" onClick={() => setShowAddComp(false)} disabled={addingComp}>
                        Cancel
                      </Button>
                      <Button type="submit" isLoading={addingComp} disabled={addingComp}>
                        {addingComp ? 'Adding...' : 'Add Comparable'}
                      </Button>
                    </div>
                  </form>
                )}

                {/* Comps List */}
                {comps.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="mx-auto w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-7 h-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-gray-900 font-medium mb-1">No comparable sales yet</h3>
                    <p className="text-gray-500 text-sm mb-4">Add comps to enable AI valuation analysis</p>
                    <Button size="sm" variant="outline" onClick={() => setShowAddComp(true)}>
                      + Add Your First Comp
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {comps.map((comp) => (
                      <div
                        key={comp.id}
                        className="border border-gray-200 rounded-xl p-4 hover:border-gray-900 transition-colors cursor-pointer group"
                        onClick={() => setSelectedComp(comp)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors">{comp.comp_address}</h4>
                              {comp.comp_property_type && (
                                <PropertyTypeBadge type={comp.comp_property_type} />
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mb-3">
                              {comp.comp_city}, {comp.comp_state} {comp.comp_zip_code}
                            </p>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                              <Metric label="Sale Price" value={comp.comp_sale_price} format="currency" size="sm" />
                              <Metric label="Sale Date" value={displayDate(comp.comp_sale_date) ?? '—'} size="sm" />
                              {comp.comp_square_footage && (
                                <Metric label="Size" value={`${formatNumber(comp.comp_square_footage)} SF`} size="sm" />
                              )}
                              {comp.comp_price_per_sqft && (
                                <Metric label="Price/SF" value={`$${comp.comp_price_per_sqft}`} size="sm" />
                              )}
                            </div>
                          </div>

                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedComp(comp);
                              }}
                              className="text-gray-500 hover:text-primary-600 hover:bg-primary-50"
                              title="View details"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteComp(comp.id);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete comp"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Property Details */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Property Details</CardTitle>
                  {!editingProperty ? (
                    <Button variant="ghost" size="sm" onClick={startEditProperty}>
                      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setEditingProperty(false)} disabled={savingProperty}>
                        Cancel
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleSavePropertyEdits} isLoading={savingProperty}>
                        Save
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editingProperty ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">List Price</label>
                      <input
                        type="number"
                        value={editForm.price}
                        onChange={(e) => setEditForm(f => ({ ...f, price: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Building Size (SF)</label>
                      <input
                        type="number"
                        value={editForm.building_size}
                        onChange={(e) => setEditForm(f => ({ ...f, building_size: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="0"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">CAP Rate (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.cap_rate}
                          onChange={(e) => setEditForm(f => ({ ...f, cap_rate: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">NOI</label>
                        <input
                          type="number"
                          value={editForm.noi}
                          onChange={(e) => setEditForm(f => ({ ...f, noi: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Property Type</label>
                      <select
                        value={editForm.property_type}
                        onChange={(e) => setEditForm(f => ({ ...f, property_type: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                      >
                        <option value="">Select type</option>
                        <optgroup label="Industrial">
                          <option value="warehouse">Warehouse</option>
                          <option value="distribution_center">Distribution Center</option>
                          <option value="manufacturing">Manufacturing</option>
                          <option value="flex_space">Flex Space</option>
                          <option value="cold_storage">Cold Storage</option>
                          <option value="industrial">Industrial (General)</option>
                        </optgroup>
                        <optgroup label="Commercial">
                          <option value="retail">Retail</option>
                          <option value="office">Office</option>
                          <option value="mixed_use">Mixed Use</option>
                        </optgroup>
                        <optgroup label="Residential">
                          <option value="multifamily">Multifamily</option>
                          <option value="residential">Residential</option>
                        </optgroup>
                        <optgroup label="Other">
                          <option value="land">Land</option>
                          <option value="other">Other</option>
                        </optgroup>
                      </select>
                    </div>
                    {/*
                      Subtype drives comp matching, and it is the single biggest lever on how
                      defensible a suggested exit cap is. The comp pool classifies 6,260 of its
                      7,747 properties this way, so an auto-service building can be priced off
                      the 98 auto-service sales that carry a cap rate rather than the median of
                      all 1,140 retail sales - which averages in drugstores and strip centres.

                      Options are labelled with how many cap-rate sales back each one, because
                      "Distribution" sounding more precise than "Warehouse" is worthless if the
                      pool only holds 28 of them: the search falls back to the whole type and
                      the extra precision bought nothing. Where a property genuinely mixes uses,
                      pick the one that pays most of the rent - that is what a buyer underwrites.
                    */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Subtype <span className="text-gray-400 font-normal">- sharpens comp matching</span>
                      </label>
                      <select
                        value={editForm.property_subtype}
                        onChange={(e) => setEditForm(f => ({ ...f, property_subtype: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                      >
                        <option value="">Not set - match on property type only</option>
                        <optgroup label="Retail">
                          <option value="standalone_retail">Standalone retail (531 comps)</option>
                          <option value="restaurant">Restaurant (113 comps)</option>
                          <option value="auto_service">Auto service (98 comps)</option>
                          <option value="strip_center">Strip center</option>
                          <option value="shopping_mall">Shopping mall</option>
                        </optgroup>
                        <optgroup label="Industrial">
                          <option value="warehouse">Warehouse (250 comps)</option>
                          <option value="manufacturing">Manufacturing (47 comps)</option>
                          <option value="distribution">Distribution (28 comps)</option>
                          <option value="flex">Flex</option>
                          <option value="cold_storage">Cold storage</option>
                          <option value="truck_terminal">Truck terminal</option>
                        </optgroup>
                        <optgroup label="Office">
                          <option value="medical_office">Medical office</option>
                          <option value="creative_office">Creative office</option>
                          <option value="office_class_a">Office - Class A</option>
                          <option value="office_class_b">Office - Class B</option>
                          <option value="office_class_c">Office - Class C</option>
                        </optgroup>
                        <optgroup label="Other">
                          <option value="self_storage">Self storage</option>
                          <option value="hospitality">Hospitality</option>
                          <option value="school">School</option>
                          <option value="other">Other</option>
                        </optgroup>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Lot Size</label>
                        <input
                          type="number"
                          value={editForm.lot_size}
                          onChange={(e) => setEditForm(f => ({ ...f, lot_size: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Year Built</label>
                        <input
                          type="number"
                          value={editForm.year_built}
                          onChange={(e) => setEditForm(f => ({ ...f, year_built: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="1990"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Zoning</label>
                        <input
                          type="text"
                          value={editForm.zoning}
                          onChange={(e) => setEditForm(f => ({ ...f, zoning: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="M-1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Parking</label>
                        <input
                          type="number"
                          value={editForm.parking_spaces}
                          onChange={(e) => setEditForm(f => ({ ...f, parking_spaces: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Occupancy (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editForm.occupancy_rate}
                        onChange={(e) => setEditForm(f => ({ ...f, occupancy_rate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="100"
                      />
                    </div>
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs font-medium text-gray-500 mb-2">Address</p>
                      <input
                        type="text"
                        value={editForm.address}
                        onChange={(e) => setEditForm(f => ({ ...f, address: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-2"
                        placeholder="Street address"
                      />
                      <div className="grid grid-cols-4 gap-2">
                        <input
                          type="text"
                          value={editForm.city}
                          onChange={(e) => setEditForm(f => ({ ...f, city: e.target.value }))}
                          className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="City"
                        />
                        <input
                          type="text"
                          value={editForm.state}
                          onChange={(e) => setEditForm(f => ({ ...f, state: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="CA"
                          maxLength={2}
                        />
                        <input
                          type="text"
                          value={editForm.zip_code}
                          onChange={(e) => setEditForm(f => ({ ...f, zip_code: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="Zip"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <dl className="space-y-4">
                    {property.apn && (
                      <Metric label="APN" value={property.apn} />
                    )}
                    <Metric label="List Price" value={formatCurrency(property.price)} />
                    {property.building_size && (
                      <Metric label="Building Size" value={`${formatNumber(property.building_size)} SF`} />
                    )}
                    {property.price_per_sqft && (
                      <Metric label="Price / SF" value={`$${property.price_per_sqft}`} />
                    )}
                    {property.cap_rate && (
                      <Metric label="CAP Rate" value={`${property.cap_rate}%`} />
                    )}
                    {property.noi && (
                      <Metric label="NOI" value={formatCurrency(property.noi)} />
                    )}
                    {property.lot_size && (
                      <Metric label="Lot Size" value={
                        property.additional_data?.lot_size_unit === 'acres'
                          ? `${(property.lot_size / 43560).toFixed(2)} acres`
                          : `${formatNumber(property.lot_size)} SF`
                      } />
                    )}
                    {property.year_built && (
                      <Metric label="Year Built" value={property.year_built} />
                    )}
                    {property.zoning && (
                      <Metric label="Zoning" value={property.zoning} />
                    )}
                    {property.parking_spaces && (
                      <Metric label="Parking" value={`${property.parking_spaces} spaces`} />
                    )}
                    {property.occupancy_rate && (
                      <Metric label="Occupancy" value={`${property.occupancy_rate}%`} />
                    )}
                    {property.submarket && (
                      <Metric label="Submarket" value={property.submarket} />
                    )}
                  </dl>
                )}
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card>
              <CardHeader>
                <CardTitle>Danger Zone</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-4">
                  Permanently delete this property and all associated data.
                </p>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDeleteProperty}
                  className="w-full"
                  leftIcon={
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  }
                >
                  Delete Property
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Transaction History */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <CardTitle>Transaction History</CardTitle>
                <p className="text-sm text-gray-500">
                  {transactions.length > 0
                    ? `${transactions.length} record${transactions.length !== 1 ? 's' : ''} from Property Database`
                    : 'No transactions found'
                  }
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {transactions.length > 0 ? (
              <div className="space-y-3">
                {transactions.map((tx) => (
                  <TransactionCard key={tx.id} tx={tx} />
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500 text-sm">No transaction history found for this property.</p>
                {txDebug && (
                  <div className="mt-3 text-left bg-gray-50 rounded-lg p-3 text-xs text-gray-500 font-mono">
                    <p>Debug: address="{txDebug.address}", city="{txDebug.city}"</p>
                    <p>Matched master IDs: {txDebug.masterIds?.length || 0}</p>
                    <p>Transactions found: {txDebug.txCount || 0}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Comp Detail Modal */}
      {selectedComp && (
        <CompDetailModal
          comp={selectedComp}
          subjectProperty={property}
          isOpen={!!selectedComp}
          onClose={() => setSelectedComp(null)}
          onUpdate={handleCompUpdate}
        />
      )}

    </Layout>
  );
}
